import React, { useState, useEffect } from 'react';
import { productsService } from '../services/productsService';
import { toast } from 'react-toastify';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Search,
  Download,
  RefreshCw,
  Image
} from 'lucide-react';
import '../styles/Products.css';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState({});
  const [selectedImages, setSelectedImages] = useState({
    product_feature_image: null,
    product_gallery1: null,
    product_gallery2: null,
    product_gallery3: null,
    product_gallery4: null
  });
  const [formData, setFormData] = useState({
    product_name: '',
    short_description: '',
    long_description: '',
    product_mrp: '',
    product_offer_price: '',
    product_feature_image: '',
    product_gallery1: '',
    product_gallery2: '',
    product_gallery3: '',
    product_gallery4: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Upload image to server
  const uploadImage = async (file) => {
    try {
      const base64 = await convertToBase64(file);
      const base64Data = base64.split(',')[1]; // Remove data:image/png;base64, prefix
      
      const response = await fetch('https://anfopublicationhouse.com/api/endpoints/upload.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: `data:${file.type};base64,${base64Data}`
        })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        return result.url;
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  // Handle image selection
  const handleImageSelect = (fieldName, file) => {
    if (file) {
      setSelectedImages(prev => ({
        ...prev,
        [fieldName]: file
      }));
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        [fieldName]: previewUrl
      }));
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productsService.getProducts();
      if (response.success) {
        setProducts(response.data || []);
      } else {
        toast.error('Failed to fetch products');
      }
    } catch (error) {
      toast.error('Error fetching products');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = () => {
    setFormData({
      product_name: '',
      short_description: '',
      long_description: '',
      product_mrp: '',
      product_offer_price: '',
      product_feature_image: '',
      product_gallery1: '',
      product_gallery2: '',
      product_gallery3: '',
      product_gallery4: ''
    });
    setSelectedImages({
      product_feature_image: null,
      product_gallery1: null,
      product_gallery2: null,
      product_gallery3: null,
      product_gallery4: null
    });
    setUploadingImages({});
    setModalMode('create');
    setShowModal(true);
  };

  const handleEditProduct = (product) => {
    setFormData({
      id: product.id,
      product_name: product.product_name,
      short_description: product.short_description,
      long_description: product.long_description,
      product_mrp: product.product_mrp,
      product_offer_price: product.product_offer_price,
      product_feature_image: product.product_feature_image || '',
      product_gallery1: product.product_gallery1 || '',
      product_gallery2: product.product_gallery2 || '',
      product_gallery3: product.product_gallery3 || '',
      product_gallery4: product.product_gallery4 || ''
    });
    setSelectedImages({
      product_feature_image: null,
      product_gallery1: null,
      product_gallery2: null,
      product_gallery3: null,
      product_gallery4: null
    });
    setUploadingImages({});
    setSelectedProduct(product);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setModalMode('view');
    setShowModal(true);
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        const response = await productsService.deleteProduct(id);
        if (response.success) {
          toast.success('Product deleted successfully');
          fetchProducts();
        } else {
          toast.error('Failed to delete product');
        }
      } catch (error) {
        toast.error('Error deleting product');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      
      // Upload all selected images first
      const updatedFormData = { ...formData };
      
      for (const fieldName of ['product_feature_image', 'product_gallery1', 'product_gallery2', 'product_gallery3', 'product_gallery4']) {
        if (selectedImages[fieldName]) {
          try {
            setUploadingImages(prev => ({ ...prev, [fieldName]: true }));
            const uploadedUrl = await uploadImage(selectedImages[fieldName]);
            updatedFormData[fieldName] = uploadedUrl;
            setUploadingImages(prev => ({ ...prev, [fieldName]: false }));
          } catch (error) {
            setUploadingImages(prev => ({ ...prev, [fieldName]: false }));
            toast.error(`Failed to upload ${fieldName.replace('_', ' ').replace('product ', '')}`);
            return;
          }
        }
      }

      let response;
      if (modalMode === 'create') {
        response = await productsService.createProduct(updatedFormData);
      } else {
        response = await productsService.updateProduct(updatedFormData);
      }

      if (response.success) {
        toast.success(`Product ${modalMode === 'create' ? 'created' : 'updated'} successfully`);
        setShowModal(false);
        fetchProducts();
        // Reset states
        setSelectedImages({
          product_feature_image: null,
          product_gallery1: null,
          product_gallery2: null,
          product_gallery3: null,
          product_gallery4: null
        });
        setUploadingImages({});
      } else {
        toast.error(response.message || 'Operation failed');
      }
    } catch (error) {
      toast.error('Error saving product');
    } finally {
      setUploading(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.short_description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="products-page">
      <div className="page-header">
        <div className="header-left">
          <h1>Products Management</h1>
          <p>Manage your product catalog and inventory</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={fetchProducts}>
            <RefreshCw size={16} />
            Refresh
          </button>
          <button className="btn btn-outline">
            <Download size={16} />
            Export
          </button>
          <button className="btn btn-primary" onClick={handleCreateProduct}>
            <Plus size={16} />
            New Product
          </button>
        </div>
      </div>

      <div className="page-controls">
        <div className="search-box">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading products...</div>
      ) : (
        <div className="products-grid">
          {filteredProducts.map(product => (
            <div key={product.id} className="product-card">
              <div className="product-image">
                {product.product_feature_image ? (
                  <img src={product.product_feature_image} alt={product.product_name} />
                ) : (
                  <div className="no-image">
                    <Image size={48} />
                  </div>
                )}
              </div>
              <div className="product-info">
                <h3 className="product-name">{product.product_name}</h3>
                <p className="product-description">{product.short_description}</p>
                <div className="product-pricing">
                  {product.product_offer_price && (
                    <span className="offer-price">${parseFloat(product.product_offer_price).toFixed(2)}</span>
                  )}
                  <span className={`mrp-price ${product.product_offer_price ? 'crossed' : ''}`}>
                    ${parseFloat(product.product_mrp || 0).toFixed(2)}
                  </span>
                </div>
                <div className="product-actions">
                  <button 
                    className="btn-icon" 
                    onClick={() => handleViewProduct(product)}
                    title="View Product"
                  >
                    <Eye size={16} />
                  </button>
                  <button 
                    className="btn-icon" 
                    onClick={() => handleEditProduct(product)}
                    title="Edit Product"
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    className="btn-icon danger" 
                    onClick={() => handleDeleteProduct(product.id)}
                    title="Delete Product"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filteredProducts.length === 0 && (
            <div className="no-data">No products found</div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal large">
            <div className="modal-header">
              <h2>
                {modalMode === 'create' && 'Create New Product'}
                {modalMode === 'edit' && 'Edit Product'}
                {modalMode === 'view' && 'Product Details'}
              </h2>
              <button 
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            {modalMode === 'view' ? (
              <div className="modal-body">
                <div className="product-details">
                  <div className="product-images">
                    {selectedProduct?.product_feature_image && (
                      <img src={selectedProduct.product_feature_image} alt={selectedProduct.product_name} />
                    )}
                  </div>
                  <div className="product-info-detailed">
                    <h3>{selectedProduct?.product_name}</h3>
                    <p className="short-desc">{selectedProduct?.short_description}</p>
                    <p className="long-desc">{selectedProduct?.long_description}</p>
                    <div className="pricing">
                      <span className="label">MRP:</span>
                      <span className="value">${parseFloat(selectedProduct?.product_mrp || 0).toFixed(2)}</span>
                    </div>
                    {selectedProduct?.product_offer_price && (
                      <div className="pricing">
                        <span className="label">Offer Price:</span>
                        <span className="value">${parseFloat(selectedProduct.product_offer_price).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="dates">
                      <span className="label">Created:</span>
                      <span className="value">{new Date(selectedProduct?.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Product Name *</label>
                    <input
                      type="text"
                      value={formData.product_name}
                      onChange={(e) => setFormData({...formData, product_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>MRP *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.product_mrp}
                      onChange={(e) => setFormData({...formData, product_mrp: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Offer Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.product_offer_price}
                      onChange={(e) => setFormData({...formData, product_offer_price: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Feature Image</label>
                    <div className="image-upload-container">
                      <div className="file-input-wrapper">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageSelect('product_feature_image', e.target.files[0])}
                          className="image-input"
                          id="feature-image"
                        />
                        <label htmlFor="feature-image" className="file-input-label">
                          {uploadingImages.product_feature_image ? (
                            'Uploading...'
                          ) : selectedImages.product_feature_image ? (
                            'Change Image'
                          ) : (
                            'Choose Feature Image'
                          )}
                        </label>
                      </div>
                      {formData.product_feature_image && (
                        <div className="image-preview">
                          <img src={formData.product_feature_image} alt="Feature preview" />
                          {uploadingImages.product_feature_image && (
                            <div className="upload-overlay">Uploading...</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-group full-width">
                    <label>Short Description *</label>
                    <textarea
                      value={formData.short_description}
                      onChange={(e) => setFormData({...formData, short_description: e.target.value})}
                      rows="3"
                      required
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Long Description</label>
                    <textarea
                      value={formData.long_description}
                      onChange={(e) => setFormData({...formData, long_description: e.target.value})}
                      rows="5"
                    />
                  </div>
                  <div className="form-group">
                    <label>Gallery Image 1</label>
                    <div className="image-upload-container">
                      <div className="file-input-wrapper">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageSelect('product_gallery1', e.target.files[0])}
                          className="image-input"
                          id="gallery-1"
                        />
                        <label htmlFor="gallery-1" className="file-input-label">
                          {uploadingImages.product_gallery1 ? (
                            'Uploading...'
                          ) : selectedImages.product_gallery1 ? (
                            'Change Image'
                          ) : (
                            'Choose Gallery Image'
                          )}
                        </label>
                      </div>
                      {formData.product_gallery1 && (
                        <div className="image-preview">
                          <img src={formData.product_gallery1} alt="Gallery 1 preview" />
                          {uploadingImages.product_gallery1 && (
                            <div className="upload-overlay">Uploading...</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Gallery Image 2</label>
                    <div className="image-upload-container">
                      <div className="file-input-wrapper">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageSelect('product_gallery2', e.target.files[0])}
                          className="image-input"
                          id="gallery-2"
                        />
                        <label htmlFor="gallery-2" className="file-input-label">
                          {uploadingImages.product_gallery2 ? (
                            'Uploading...'
                          ) : selectedImages.product_gallery2 ? (
                            'Change Image'
                          ) : (
                            'Choose Gallery Image'
                          )}
                        </label>
                      </div>
                      {formData.product_gallery2 && (
                        <div className="image-preview">
                          <img src={formData.product_gallery2} alt="Gallery 2 preview" />
                          {uploadingImages.product_gallery2 && (
                            <div className="upload-overlay">Uploading...</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Gallery Image 3</label>
                    <div className="image-upload-container">
                      <div className="file-input-wrapper">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageSelect('product_gallery3', e.target.files[0])}
                          className="image-input"
                          id="gallery-3"
                        />
                        <label htmlFor="gallery-3" className="file-input-label">
                          {uploadingImages.product_gallery3 ? (
                            'Uploading...'
                          ) : selectedImages.product_gallery3 ? (
                            'Change Image'
                          ) : (
                            'Choose Gallery Image'
                          )}
                        </label>
                      </div>
                      {formData.product_gallery3 && (
                        <div className="image-preview">
                          <img src={formData.product_gallery3} alt="Gallery 3 preview" />
                          {uploadingImages.product_gallery3 && (
                            <div className="upload-overlay">Uploading...</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Gallery Image 4</label>
                    <div className="image-upload-container">
                      <div className="file-input-wrapper">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageSelect('product_gallery4', e.target.files[0])}
                          className="image-input"
                          id="gallery-4"
                        />
                        <label htmlFor="gallery-4" className="file-input-label">
                          {uploadingImages.product_gallery4 ? (
                            'Uploading...'
                          ) : selectedImages.product_gallery4 ? (
                            'Change Image'
                          ) : (
                            'Choose Gallery Image'
                          )}
                        </label>
                      </div>
                      {formData.product_gallery4 && (
                        <div className="image-preview">
                          <img src={formData.product_gallery4} alt="Gallery 4 preview" />
                          {uploadingImages.product_gallery4 && (
                            <div className="upload-overlay">Uploading...</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={uploading}>
                    {uploading ? 'Uploading...' : (modalMode === 'create' ? 'Create Product' : 'Update Product')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;